
import React from "react";
import { Card } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { ChevronRight } from "lucide-react";
import { MenuItem, menuItems } from "./SettingsMenuItems";

interface OrganizationSettingsSidebarProps {
  userName: string;
  userEmail: string;
  activeMenu: string;
  setActiveMenu: (menuId: string) => void;
}

export const OrganizationSettingsSidebar: React.FC<OrganizationSettingsSidebarProps> = ({
  userName,
  userEmail,
  activeMenu,
  setActiveMenu
}) => {
  return (
    <div className="md:col-span-1">
      <Card className="p-0 overflow-hidden shadow-md border-0 dark:border dark:border-gray-800 rounded-xl dark:bg-gray-800/50">
        {/* Header with avatar and name */}
        <div className="p-6 bg-white dark:bg-[#121624] border-b border-gray-100 dark:border-[rgba(60,63,84,0.6)]">
          <div className="flex items-center space-x-4">
            <Avatar className="bg-gradient-to-br from-purple-500 to-indigo-600 dark:from-purple-400 dark:to-indigo-500 h-12 w-12 shadow-lg ring-2 ring-white dark:ring-gray-800">
              <span className="text-lg font-medium text-white flex items-center justify-center h-full w-full">{userName.charAt(0)}</span>
            </Avatar>
            <div>
              <p className="font-semibold text-gray-800 dark:text-gray-100">{userName}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{userEmail}</p>
            </div>
          </div>
        </div>
        
        {/* Menu list */}
        <div className="py-2 dark:bg-[#121624]">
          {menuItems.map((item) => (
            <div key={item.id}>
              <div 
                className={`group flex items-center px-6 py-3.5 cursor-pointer transition-all duration-200 
                  ${activeMenu === item.id 
                    ? 'bg-[#f5f0ff] dark:bg-[#8b5cf6]/20 border-l-4 border-[#8b5cf6] dark:border-[#a78bfa] pl-[1.35rem]' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/40 border-l-4 border-transparent'}`}
                onClick={() => setActiveMenu(item.id)}
              >
                <div className={`mr-3 transition-colors duration-200 ${
                  activeMenu === item.id 
                    ? 'text-[#8b5cf6] dark:text-[#a78bfa]' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'
                }`}>
                  {item.icon}
                </div>
                <span className={`transition-colors duration-200 ${
                  activeMenu === item.id 
                    ? 'text-[#8b5cf6] dark:text-[#a78bfa] font-medium' 
                    : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100'
                }`}>
                  {item.label}
                </span>
                
                {item.children && (
                  <ChevronRight 
                    size={16} 
                    className={`ml-auto transition-transform duration-200 ${
                      item.isExpanded ? 'rotate-90' : ''
                    } ${
                      activeMenu === item.id 
                        ? 'text-[#8b5cf6] dark:text-[#a78bfa]' 
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  />
                )}
              </div>
              
              {/* Submenu for items that have children */}
              {item.children && item.isExpanded && (
                <div className="pl-14 bg-white dark:bg-[#121624]">
                  {item.children.map(child => (
                    <div 
                      key={child.id}
                      className={`py-2.5 cursor-pointer transition-colors duration-200
                        ${activeMenu === child.id 
                          ? 'text-[#8b5cf6] dark:text-[#a78bfa] font-medium' 
                          : 'text-gray-600 dark:text-gray-400 hover:text-[#8b5cf6] dark:hover:text-[#a78bfa]'}`}
                      onClick={() => setActiveMenu(child.id)}
                    >
                      {child.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
